import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Stack, Box, Button, Typography, Divider, DialogActions, DialogContentText, TextField, Paper } from "@mui/material";
import axios from "axios";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import _, { set } from "lodash";
import CircularProgress from "@mui/material/CircularProgress";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { useContext } from "react";
import { NotificationType } from "../../../constants/global";
import { getDisplayProduct } from "../../../components/utils/utils";

interface Props {
  editOrderID: string;
  setEditOrderID: (id: string | null) => void;
}

interface ProductItem {
  id: string;
  drugName: string;
  expDate: Dayjs;
  packageCondition: boolean;
  lotNumber: string;
  quantity?: number; // Optional field for quantity
  status?: string; // Optional field for status
  // ...other fields if needed
}

const EditOrder: React.FC<Props> = ({ editOrderID, setEditOrderID }) => {
  const [initialData, setInitialData] = useState<ProductItem[]>([]);
  const [productData, setProductData] = useState<ProductItem[]>([]);
  const [deletedProductIds, setDeletedProductIds] = useState<number[]>([]);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [refetchProducts, setRefetchProducts] = useState(false);
  const { addToast } = useContext(ToasterContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  useEffect(() => {
    if (!editOrderID) return;
    setLoading(true);
    const api = `${process.env.REACT_APP_API_URL}/v2/order/get/orderByOrderNumber?orderNumber=${editOrderID}`;
    axios
      .get(api)
      .then((response) => {
        const tempArr: ProductItem[] = response.data.data.map((item: any) => {
          const displayProduct = getDisplayProduct(item);
          return {
          id: item.Product.id,
          drugName: displayProduct.drugName,
          expDate: dayjs(displayProduct.expDate),
          // packageCondition: displayProduct.packageCondition,
          lotNumber: displayProduct.lotNumber || "",
          quantity: item.quantity || 0,
          status: "active",
        };
        });
        setInitialData(tempArr);
        setProductData(tempArr);
        setSelectedIndex(0);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [editOrderID, refetchProducts]);

  const handleDrugSelect = (index: number) => {
    setSelectedIndex(index);
  };

  const updateField = (field: keyof ProductItem, value: any) => {
    setProductData((prev) => {
      const newData = [...prev];
      newData[selectedIndex] = {
        ...newData[selectedIndex],
        [field]: value,
      };
      return newData;
    });
  };

  const handleSave = () => {
    // validation, so that quantity can not be more than initial quantity
    setConfirmationOpen(false);
    const initialQuantity = initialData[selectedIndex].quantity || 0;
    const newQuantity = productData[selectedIndex].quantity || 0;
    if (newQuantity > initialQuantity || newQuantity <= 0) {
      alert("Quantity cannot be more than initial quantity, and must be greater than 0");
      return;
    }

    setEditing(true);
    const payload = productData.map((item) => ({
      orderNumber: editOrderID,
      productId: item.id,
      expDate: item.expDate ? item.expDate.toISOString() : null,
      // packageCondition: item.packageCondition,
      lotNumber: item.lotNumber,
      quantity: item.quantity,
      status: item.status || "active", // Ensure status is set to 'active' or 'deleted'
    }));

    // check that lot number and quanity are only numbers
    payload.forEach((item) => {
      if (item.quantity === undefined || isNaN(item.quantity) || item.quantity <= 0) {
        addToast({ type: NotificationType.Error, title: "Quantity must be a valid number greater than 0." });
        setEditing(false);
        return
      }
      if (!/^\d+$/.test(item.lotNumber)) {
        addToast({ type: NotificationType.Error, title: "Lot number must be a valid number." });
        setEditing(false);
        return
      }
    })

    const api = `${process.env.REACT_APP_API_URL}/v2/order/edit/OpenOrder`;
    axios
      .post(api, payload)
      .then((response) => {
        setEditOrderID(null);
        setProductData([]);
        setSelectedIndex(0);
        setEditing(false);
        addToast({ type: NotificationType.Success, title: "Order has been edited successfully. Waiting for buyer re-confirmation" });
        setTimeout(() => {
          window.location.reload();
        }, 2500);
      })
      .catch((error) => {
        console.error("Error saving data:", error);
      });
  };

  const handleClose = () => {
    setEditOrderID(null);
    setProductData([]);
    setSelectedIndex(0);
  };

  const handleDelete = (productId: number) => {
    window.confirm("Are you sure you want to delete this order? This action cannot be undone.");

    // check if its the last item thats active, if so, do not allow deletion
    const activeItems = productData.filter((item) => item.status !== "deleted");
    if (activeItems.length <= 1) {
      addToast({ type: NotificationType.Error, title: "Cannot delete the last active item in the order." });
      return;
    }

    const currentPrdocutData = [...productData];
    const updatedProductData = currentPrdocutData.map((item) => {
      if (item.id == productId.toString()) {
        return { ...item, status: "deleted" };
      }
      return item;
    });
    setProductData(updatedProductData);
  };

  const handleRestore = (productId: number) => {
    const currentPrdocutData = [...productData];
    const updatedProductData = currentPrdocutData.map((item) => {
      if (item.id == productId.toString()) {
        return { ...item, status: "active" };
      }
      return item;
    });
    setProductData(updatedProductData);
  };

  const selectedDrug = productData[selectedIndex];

  return (
    <Dialog fullWidth maxWidth="sm" open={!!editOrderID} onClose={handleClose}>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <DialogContent>
          <Dialog fullWidth maxWidth="sm" open={confirmationOpen} onClose={() => setConfirmationOpen(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">Edit</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">Are you sure you want to edit this order?</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setConfirmationOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={() => handleSave()}>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <DialogTitle>Edit Order - #{editOrderID}</DialogTitle>
          <Stack spacing={3}>
            <Box display="flex" flexWrap="wrap" gap={1} justifyContent="flex-start" sx={{ maxHeight: 200, overflowY: "auto" }}>
              {productData.map((item, index) =>
                item.status !== "deleted" ? (
                  <Button
                    key={item.id}
                    variant={selectedIndex === index ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleDrugSelect(index)}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                  >
                    {item.drugName}
                  </Button>
                ) : (
                  <Button
                    key={item.id}
                    variant={selectedIndex === index ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleDrugSelect(index)}
                    sx={{ borderRadius: 2, textTransform: "none", backgroundColor: "oklch(70.4% 0.191 22.216)" }}
                  >
                    {item.drugName + " - Deleted"}
                  </Button>
                )
              )}
            </Box>

            <Divider />

            {selectedDrug && (
              <Paper elevation={2} sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {selectedDrug.status !== "deleted" ? (
                    <Button
                      size="small"
                      style={{
                        backgroundColor: "oklch(70.4% 0.191 22.216)",
                        color: "white",
                        width: "fit-content",
                      }}
                      onClick={() => {
                        handleDelete(parseInt(selectedDrug.id, 10));
                      }}
                    >
                      Delete
                    </Button>
                  ) : (
                    <>
                      {" "}
                      <Button
                        size="small"
                        style={{
                          backgroundColor: "oklch(79.2% 0.209 151.711)",
                          color: "white",
                          width: "fit-content",
                        }}
                        onClick={() => {
                          handleRestore(parseInt(selectedDrug.id, 10));
                        }}
                      >
                        Restore
                      </Button>
                    </>
                  )}

                  <Typography variant="h6">{selectedDrug.drugName}</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={selectedDrug.expDate}
                      onChange={(newDate) => updateField("expDate", newDate || dayjs())}
                      views={["month", "year"]}
                      disablePast
                      format="MM/YYYY"
                      minDate={selectedDrug.expDate}
                      label="Expiration Date"
                      slotProps={{
                        textField: { fullWidth: true, variant: "outlined" },
                      }}
                    />
                  </LocalizationProvider>

                  {/* <FormControlLabel control={<Switch checked={selectedDrug.packageCondition} onChange={(e) => updateField("packageCondition", e.target.checked ? 1 : 0)} />} label="Sealed" /> */}

                  <TextField label="Lot Number" value={selectedDrug.lotNumber} onChange={(e) => updateField("lotNumber", e.target.value)} fullWidth />
                  <TextField label="Quantity" value={selectedDrug.quantity} onChange={(e) => updateField("quantity", e.target.value)} fullWidth />
                </Stack>
              </Paper>
            )}

            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} pt={2}>
              {editing && (
                <CircularProgress
                  size={24}
                  sx={{
                    height: "full",
                  }}
                />
              )}
              <Button
                disabled={_.isEqual(initialData, productData)}
                onClick={() => {
                  setConfirmationOpen(true);
                }}
                variant="contained"
                size="large"
              >
                Save Changes
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default EditOrder;
